import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { FiFileText, FiDollarSign, FiMessageCircle, FiMessageSquare, FiSearch, FiX } from 'react-icons/fi';
import { apiJson } from '@/lib/api';
import { track } from '@/lib/analytics';
import { useHouseholds } from '@/lib/household-context';
import { setPendingChatFocus } from '@/lib/chatFocus';
import type { MessageSearchHit, MessageSearchResponse, SelectedConversation } from '@/lib/types';

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;

const nunito = {
  fontFamily:
    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
};

function formatRelativeTime(timestamp: string): string {
  const date = new Date(timestamp);
  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function highlightMatch(text: string, query: string) {
  const index = text.toLowerCase().indexOf(query.toLowerCase());
  if (index < 0) return text;
  return (
    <>
      {text.slice(0, index)}
      <mark className='rounded-sm bg-primary/15 px-0.5 text-inherit'>
        {text.slice(index, index + query.length)}
      </mark>
      {text.slice(index + query.length)}
    </>
  );
}

function snippetAroundMatch(text: string, query: string, radius = 48): string {
  const index = text.toLowerCase().indexOf(query.toLowerCase());
  if (index < 0 || text.length <= radius * 2) return text;
  const start = Math.max(0, index - radius);
  const end = Math.min(text.length, index + query.length + radius);
  return `${start > 0 ? '…' : ''}${text.slice(start, end)}${end < text.length ? '…' : ''}`;
}

export function hitToConversation(hit: MessageSearchHit): SelectedConversation {
  if (hit.chat_type === 'custom-chat' && hit.thread_id != null) {
    return {
      type: 'custom-chat',
      chatId: hit.thread_id,
      title: hit.thread_title,
      isArchived: false,
      isCreator: false,
    };
  }
  if (hit.chat_type === 'expense-dispute' && hit.thread_id != null) {
    return { type: 'expense-dispute', disputeId: hit.thread_id, itemId: 0 };
  }
  if (hit.chat_type === 'payment-dispute' && hit.thread_id != null) {
    return { type: 'payment-dispute', disputeId: hit.thread_id, itemId: 0 };
  }
  return { type: 'main' };
}

export function ChatSearchInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className='relative mt-3'>
      <FiSearch className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' />
      <input
        type='text'
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder='Search all chats'
        aria-label='Search all chats'
        className='w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-9 pr-9 text-sm text-gray-900 placeholder-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20'
        style={nunito}
      />
      {value ? (
        <button
          type='button'
          onClick={() => onChange('')}
          className='absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600'
          aria-label='Clear search'
        >
          <FiX className='h-4 w-4' />
        </button>
      ) : null}
    </div>
  );
}

export function ChatSearchResults({
  query,
  onSelect,
}: {
  query: string;
  onSelect: (conversation: SelectedConversation) => void;
}) {
  const { getToken } = useAuth();
  const { currentHouseholdId } = useHouseholds();
  const [debouncedQuery, setDebouncedQuery] = useState(query);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [query]);

  const enabled = debouncedQuery.trim().length >= MIN_QUERY_LENGTH;

  const searchQuery = useQuery({
    queryKey: ['message-search', currentHouseholdId, debouncedQuery.trim()],
    queryFn: () => {
      const params = new URLSearchParams({
        q: debouncedQuery.trim(),
        limit: '50',
      });
      return apiJson<MessageSearchResponse>(
        getToken,
        `/v1/messages/search?${params.toString()}`,
      );
    },
    enabled,
  });

  useEffect(() => {
    if (!enabled || !searchQuery.isSuccess || !searchQuery.data) return;
    track('Search Chat Messages', {
      query: debouncedQuery.trim(),
      result_count: searchQuery.data.total_count,
    });
  }, [
    enabled,
    searchQuery.isSuccess,
    searchQuery.dataUpdatedAt,
    searchQuery.data,
    debouncedQuery,
  ]);

  const results = searchQuery.data?.results ?? [];

  const emptyLabel = useMemo(() => {
    if (searchQuery.isLoading) return null;
    if (searchQuery.isError) return 'Couldn’t search messages. Try again.';
    if (enabled && results.length === 0) return 'No messages match that search.';
    return null;
  }, [enabled, results.length, searchQuery.isError, searchQuery.isLoading]);

  return (
    <div className='flex-1 overflow-y-auto px-4 py-3'>
      {searchQuery.isLoading ? (
        <div className='flex items-center justify-center py-12'>
          <div className='h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent' />
        </div>
      ) : emptyLabel ? (
        <p className='py-8 text-center text-sm text-gray-500' style={nunito}>
          {emptyLabel}
        </p>
      ) : (
        <div className='space-y-2'>
          {searchQuery.data && searchQuery.data.total_count > 0 ? (
            <p className='px-1 pb-1 text-xs font-semibold uppercase tracking-wider text-gray-500'>
              {searchQuery.data.total_count}{' '}
              {searchQuery.data.total_count === 1 ? 'result' : 'results'}
            </p>
          ) : null}
          {results.map((hit) => (
            <SearchHitCard
              key={hit.id}
              hit={hit}
              query={debouncedQuery.trim()}
              onSelect={() => {
                setPendingChatFocus({
                  messageId: hit.id,
                  timestamp: hit.timestamp,
                });
                onSelect(hitToConversation(hit));
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SearchHitCard({
  hit,
  query,
  onSelect,
}: {
  hit: MessageSearchHit;
  query: string;
  onSelect: () => void;
}) {
  const Icon =
    hit.chat_type === 'custom-chat'
      ? FiMessageSquare
      : hit.chat_type === 'expense-dispute'
        ? FiFileText
        : hit.chat_type === 'payment-dispute'
          ? FiDollarSign
          : FiMessageCircle;
  const iconWrap =
    hit.chat_type === 'custom-chat'
      ? 'bg-indigo-500'
      : hit.chat_type === 'expense-dispute' || hit.chat_type === 'payment-dispute'
        ? 'bg-amber-500'
        : 'bg-primary';

  return (
    <button
      type='button'
      onClick={onSelect}
      className='flex w-full items-start gap-3 rounded-2xl border border-gray-200 bg-white p-3.5 text-left transition-colors hover:border-gray-300 hover:shadow-sm'
    >
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${iconWrap}`}
      >
        <Icon className='h-5 w-5 text-white' />
      </div>
      <div className='min-w-0 flex-1'>
        <div className='flex items-center justify-between gap-2'>
          <span className='truncate text-sm font-bold text-gray-900' style={nunito}>
            {hit.thread_title}
          </span>
          <span className='shrink-0 text-xs text-gray-400'>
            {formatRelativeTime(hit.timestamp)}
          </span>
        </div>
        <p className='mt-0.5 text-sm text-gray-600' style={nunito}>
          <span className='font-semibold text-gray-800'>
            {hit.sender_first_name ?? 'Someone'}:
          </span>{' '}
          {highlightMatch(snippetAroundMatch(hit.content, query), query)}
        </p>
      </div>
    </button>
  );
}

export const CHAT_SEARCH_MIN_LENGTH = MIN_QUERY_LENGTH;
